// EmailHistoryDialog.tsx

import React, {useEffect, useMemo, useState} from "react";
import {CircularProgress, Grid2} from "@mui/material";
import {MailDTO, MailStatus} from "@/api/data-contracts";
import {DataTable} from "@/lib/components/DataTable";
import dayjs from "dayjs";
import {companyApi, requestApi} from "@/boxhub-api";
import {DateFormatter} from "@/lib/components/DateFormate";
import {useSnackbarEx} from "@/lib/snackbarex";
import {useTranslation} from "react-i18next";
import { CommonI18N, NotificationI18N } from "@/translations";
import {DialogEx} from "@/lib/components/DialogEx";
import Box from "@mui/material/Box";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {CellContext, ColumnDef} from "@tanstack/react-table";
import {Badge} from "@/lib/components/Badge";
import {color_green, color_purple, color_red} from "@/agentur/AgenturColors";

type SendStatusCellProps = {status: MailStatus | undefined};

const SendStatusCell: React.FC<SendStatusCellProps> = ({status}) => {
    const {t} = useTranslation();
    const getStatusText = () => {
        if (status === MailStatus.SENT) {
            return t(CommonI18N.enum.emailStatus[MailStatus.SENT]);
        }
        if (status === MailStatus.ERROR) {
            return t(CommonI18N.enum.emailStatus[MailStatus.ERROR]);
        }
        return t(CommonI18N.enum.emailStatus[MailStatus.NOT_SENT]);
    };
    const getStatusColor = () => {
        if (status === MailStatus.SENT) {
            return color_green[2];
        }
        if (status === MailStatus.ERROR) {
            return color_red[2];
        }
        return color_purple[2];
    };
    return <Badge label={getStatusText()} backgroundColor={getStatusColor()}/>;
};

type CompanyCellProps = {name: string; detail: string | undefined};

const CompanyContactCell: React.FC<CompanyCellProps> = ({name, detail}) => (
    <Grid2 container direction="column">
        <Grid2>{name}</Grid2>
        <Grid2 fontSize="88%" fontWeight="bold">{detail}</Grid2>
    </Grid2>
);

const renderSendStatusCell = (info: CellContext<MailDTO, MailStatus | undefined>) => (
    <SendStatusCell status={info.getValue()}/>
);

const renderSenderCell = (info: CellContext<MailDTO, {name?: string} | undefined>) => (
    <CompanyContactCell name={info.getValue()?.name ?? ""} detail={info.row.original.from}/>
);

const renderRecipientCell = (info: CellContext<MailDTO, {name?: string} | undefined>) => (
    <CompanyContactCell name={info.getValue()?.name ?? ""} detail={info.row.original.to?.join(", ")}/>
);

/**
 * Props for the EmailHistoryDialog component.
 *
 * @property {boolean} open - Whether the dialog is open.
 * @property {number} requestId - The ID of the request to fetch email history for.
 * @property {() => void} onClose - The function to call when the dialog is closed.
 * @property {boolean} [enableSendStatus=true] - If true, enables the send status column.
 */
interface EmailHistoryDialogProps {
    open: boolean;
    requestId?: number;
    onClose: () => void;
    hideSendStatus?: boolean;
}

/**
 * EmailHistoryDialog component.
 *
 * @param {EmailHistoryDialogProps} props - The props for the component.
 * @returns {React.ReactElement} The rendered EmailHistoryDialog component.
 */
const EmailHistoryDialog: React.FC<EmailHistoryDialogProps> = ({
                                                                   open,
                                                                   requestId,
                                                                   onClose,
                                                                   hideSendStatus
                                                               }: EmailHistoryDialogProps): React.ReactElement => {
    const {t} = useTranslation();
    const [emails, setEmails] = useState<MailDTO[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const dateFormat = DateFormatter("date_hm");
    const {showMessage} = useSnackbarEx();

    // Table columns
    const columns = useMemo<ColumnDef<MailDTO, unknown>[]>(
            () => [
                {
                    header: t(NotificationI18N.emailHistory.column.createdAt),
                    accessorKey: "createdAt",
                    cell: (info: CellContext<MailDTO, string>) => dayjs(info.getValue()).format(dateFormat)
                },
                {
                    header: t(NotificationI18N.emailHistory.column.sentAt),
                    accessorFn: (row) => row.sent,
                    cell: (info: CellContext<MailDTO, string | null | undefined>) =>
                            info.getValue() ? dayjs(info.getValue()).format(dateFormat) : t(CommonI18N.enum.emailStatus.NOT_SENT)
                },
                ...(hideSendStatus
                        ? []
                        : [
                            {
                                header: t(NotificationI18N.emailHistory.column.sendStatus),
                                accessorFn: (row: MailDTO) => row.status,
                                cell: renderSendStatusCell
                            }
                        ]),
                {
                    header: t(NotificationI18N.emailHistory.column.sender),
                    accessorKey: "sender",
                    accessorFn: (row) => row.sender,
                    cell: renderSenderCell
                },
                {
                    header: t(NotificationI18N.emailHistory.column.recipient),
                    accessorFn: (row) => row.receiver,
                    cell: renderRecipientCell
                },
                {
                    header: t(NotificationI18N.emailHistory.column.subject),
                    accessorFn: (row) => row.subject,
                    cell: (info: CellContext<MailDTO, string>) => info.getValue() || ""
                }
            ],
            [dateFormat, hideSendStatus, t]
    );

    // Fetch emails when dialog opens
    useEffect(() => {
        if (open) {
            setLoading(true);
            const fetch =
                    (requestId && requestApi.getRequestEMailHistory(requestId)) || companyApi.getCompanyEMailHistory();
            fetch
                    .then((response) => {
                        setEmails(response.data.mails ?? []);
                    })
                    .catch((error) => {
                        showMessage({summary: t(CommonI18N.message.unknownError), severity: "error", error});
                    })
                    .finally(() => {
                        setLoading(false);
                    });
        }
    }, [open, requestId, showMessage, t]);

    return (
            <DialogEx
                    open={open}
                    onClose={onClose}
                    maxWidth="xl"
                    fullWidth
                    title={t(NotificationI18N.emailHistory.page.title)}
                    allowFullScreen
                    buttons={<ButtonEx label={t(CommonI18N.action.close)} color="secondary" onClick={onClose}/>}
            >
                <Box
                        style={{
                            height: "600px",
                            overflowY: "auto"
                        }}
                >
                    {loading ? (
                            <CircularProgress/>
                    ) : (
                            <DataTable
                                    columns={columns}
                                    data={emails}
                                    noResultsValue={t(NotificationI18N.emailHistory.message.noEmails)}
                                    expandable
                                    renderRowSubComponent={(row) => (
                                            <div>
                                                <strong>{t(NotificationI18N.emailHistory.field.message)}:</strong>
                                                <div
                                                        style={{marginTop: "10px"}}
                                                        dangerouslySetInnerHTML={{__html: row.original?.message || ""}}
                                                />
                                            </div>
                                    )}
                                    maxHeight={550}
                            />
                    )}
                </Box>
            </DialogEx>
    );
};

export default EmailHistoryDialog;
